/**
 * The canonical list of every icon in the app: one entry per component under
 * `shared/components/icons`, keyed by the name the rest of the codebase uses to
 * refer to it, with a description of what the glyph shows and what it is
 * typically used for.
 *
 * This file is the single source of truth for icon names:
 * - `PillarIcon` (shared/components/icons/PillarIcon.tsx) is keyed to it, so a
 *   name added here without a component — or a component registered under a
 *   name missing here — fails to compile.
 * - Icon names stored in the database (`AppRouteConfig.icon_name`,
 *   `RefConferenceEventType.event_type_icon`, `RefSocietySubmissionType.icon`)
 *   are plain TEXT validated against these names. Renaming a key therefore
 *   needs a data migration; the odd `chatbubblesOutline` spelling is stored.
 *
 * To add a new icon: create the component under `shared/components/icons`, add
 * its name and description here, and register the component in `PillarIcon`.
 * The `pillar/icon-registered` lint rule fails on an icon file that is not
 * registered. See docs/design/DESIGN_ICONS.MD.
 *
 * This module must stay free of React imports so that seeds, API code, and
 * Cypress can import names without pulling in components.
 */
export const PILLAR_ICON_DESCRIPTIONS = {
  // Ionicons-style outline glyphs sized for the hub side menu
  bookOutline: "An open book; reading, education, or publications sections.",
  cashOutline: "A banknote; payments, dues, or financial sections.",
  chatbubblesOutline: "Two overlapping speech bubbles; chats and discussions.",
  easelOutline: "A presentation easel; courses, sessions, or presentations.",
  folderOpenOutline: "An open folder; documents and file collections.",
  listOutline: "Stacked horizontal lines; lists and indexes.",
  mailOutline: "An envelope; mail, messages, or contact sections.",
  peopleOutline: "Two people; membership, directories, or communities.",
  ribbonOutline: "An award ribbon; awards, honors, or achievements.",
  idCardOutline: "An ID badge; profiles or credentials.",
  fileTrayFullOutline: "A full inbox tray; submissions or received items.",
  todayOutline: "A calendar marked on today; schedules and events.",
  fileTrayStackedOutline: "Two stacked inbox trays; archives or grouped items.",

  // People
  user: "A single person; an individual profile or account.",
  userCircle: "A person inside a circle; the signed-in user or an avatar.",
  userMinus: "A person with a minus; removing someone from a list or group.",
  userPassword: "A person with password dots; credentials and login settings.",
  userPlus: "A person with a plus; inviting or adding someone.",
  users: "Two people; groups, members, or shared access.",
  impersonate: "A person with a mask; acting as another user.",

  // Arrows and movement
  arrowLeft: "An arrow pointing left; going back.",
  arrowPath: "Arrows in a loop; refresh, retry, or a recurring process.",
  arrowsMaximize: "Arrows pointing outward; expanding to full size.",
  arrowsSplit: "One line splitting into two; branching or divergence.",
  rotateClockwise: "A circular arrow; rotate, redo, or reset.",
  merge: "Two lines joining into one; merging records.",
  gitFork: "A branch diagram; workflows or version-style branching.",
  externalLink: "A box with an arrow leaving it; opens outside the app.",
  logOutOutline: "A doorway with an arrow; signing out.",
  download: "An arrow into a tray; downloading to the device.",
  send: "A paper plane; sending a message.",

  // Chevrons and carets
  chevronBack: "A thin left chevron; back navigation (ionicons style).",
  chevronDown: "A downward chevron; expands a collapsed section.",
  chevronExpandOutline: "Paired up/down chevrons; expandable selector.",
  chevronLeft: "A left chevron; previous item or collapse.",
  chevronRight: "A right chevron; next item or drill-in.",
  chevronUp: "An upward chevron; collapses an expanded section.",
  chevronUpDown: "An up-down chevron pair; sortable or select control.",
  chevronsLeft: "A double left chevron; collapse a panel or jump to start.",
  chevronsRight: "A double right chevron; expand a panel or jump to end.",
  caretLeft: "A small solid left triangle; previous.",
  caretRight: "A small solid right triangle; next.",

  // Charts and reporting
  chartArrows: "A chart with arrows; trends or comparisons.",
  chartBarSquare: "A bar chart in a square; dashboards.",
  chartHistogram: "A histogram; distributions or workflow analytics.",
  chartPie: "A pie chart; proportional breakdowns.",
  reports: "A document with chart lines; the reports section.",
  presentation: "A presentation board; conferences or sessions.",

  // Communication
  chatBubble: "A speech bubble; comments or messaging.",
  chatBubbleLeft: "A left-aligned speech bubble; a conversation thread.",
  chatBubbleOvalLeft: "An oval speech bubble; a chat entry point.",
  bell: "A bell; notifications.",
  envelopeFilled: "A solid envelope; unread mail or email actions.",
  mailForward: "An envelope with an arrow; forwarding a message.",
  headphonesFilled: "Solid headphones; support or audio.",

  // Files and documents
  documentOutline: "A single document outline; a generic document.",
  fileDollar: "A document with a dollar sign; invoices or financial docs.",
  fileDownload: "A document with a down arrow; downloadable file.",
  fileExport: "A document with an outgoing arrow; exporting a file or data.",
  fileReport: "A document with a chart; a generated report file.",
  fileSymlink: "A document with a corner arrow; a linked or shortcut file.",
  fileText: "A document with text lines; text content.",
  fileTrayOutline: "An inbox tray; incoming items.",
  clipboard: "A clipboard; forms or copied content.",
  clipboardCheck: "A clipboard with a check; completed review or approval.",
  clipboardPlus: "A clipboard with a plus; adding a form or record.",
  copy: "Two overlapping rectangles; copy to clipboard.",
  photo: "A framed picture; images and photos.",

  // Books and bookmarks
  book: "A closed book; documentation or catalogs.",
  bookMark: "A bookmark ribbon; saved items.",
  bookmarkSquare: "A bookmark on a square; saved pages or references.",

  // Calendar and time
  calendar: "A calendar grid; dates.",
  calendarDays: "A calendar with day dots; date pickers and schedules.",
  calendarEvent: "A calendar with a marked day; a scheduled event.",
  calendarWithPlus: "A calendar with a plus; adding an event.",
  clock: "A clock face; times and durations.",

  // Money and commerce
  coin: "A coin; currency amounts or pricing.",
  creditCard: "A credit card; payment methods.",
  receiptOff: "A crossed-out receipt; a voided or non-receipt item.",
  receiptTax: "A receipt with a percent sign; taxes and rates.",
  cardAmex: "The American Express card brand mark.",
  cardGeneric: "A generic card face for unrecognized card brands.",
  cardMasterCard: "The Mastercard card brand mark.",
  cardVisa: "The Visa card brand mark.",

  // Status and signs
  check: "A checkmark; success or a completed step.",
  checkCircle: "A checkmark inside a circle; a confirmed state.",
  cross: "An X; close, dismiss, or a negative state.",
  crossCircleFilled: "A solid circle with an X; errors or removal.",
  exclamationCircle: "An exclamation in a circle; warnings.",
  alertTriangle: "An exclamation in a triangle; caution or destructive risk.",
  info: "A lowercase i in a circle; informational hints.",
  minus: "A minus sign; removing or decrementing.",
  minusCircle: "A minus in a circle; removing an item.",
  plus: "A plus sign; adding or incrementing.",
  plusSmall: "A plus sign with a small default size; inline add actions.",
  plusSquare: "A plus inside a square; adding a contained item.",
  sparkles: "Sparkles; AI-assisted or highlighted features.",
  star: "A five-pointed star; favorites or ratings.",
  dot: "A single small dot; a status or separator marker.",
  dotsEllipsis: "Three horizontal dots; an overflow menu.",
  dotsVertical: "Three vertical dots; a row-level actions menu.",

  // Visibility and security
  eye: "An open eye; viewing or making visible.",
  eyeOff: "An eye with a slash; hidden or masked content.",
  lock: "A padlock; locked or restricted content.",
  lockClosed: "A closed padlock, alternate style; secured content.",
  lockOpen: "An open padlock; unlocked content.",

  // Editing
  pencil: "A pencil; editing.",
  pencilEdit: "A pencil over a line; editing a field.",
  pencilSquare: "A pencil over a square; editing a record or page.",
  eraser: "An eraser; clearing content.",
  scissors: "Scissors; cutting or splitting.",
  trash: "A trash can; deleting.",
  trashFilled: "A solid trash can; deleting, emphasized.",
  trashOutline: "A trash can outline (ionicons style); deleting.",

  // Layout and structure
  layoutColumns: "Two columns; column layout controls.",
  layoutSidebarLeftExpand:
    "A panel expanding from the left; open left sidebar.",
  layoutSidebarRightExpand:
    "A panel expanding from the right; open right sidebar.",
  columnsThree: "Three columns; table column configuration.",
  list: "Stacked lines; a list view.",
  gripVertical: "Two vertical dot rails; a drag handle.",
  adjustmentsHorizontal: "Horizontal slider bars; filters and settings.",
  settingsGear: "A gear; settings.",
  circleSquare: "A circle overlapping a square; mixed content or shapes.",
  boxMultiple: "Stacked boxes; batches or collections.",
  stack2: "Two stacked layers; layered content or duplicates.",

  // Pins and tags
  pin: "A map pin; locations.",
  pinned: "A pushpin; pinned items.",
  pinnedOff: "A crossed-out pushpin; unpinning.",
  tag: "A price tag; tags and labels.",
  link: "Two chain links; hyperlinks or linked records.",

  // Cloud
  cloudDownload: "A cloud with a down arrow; downloading from the server.",
  cloudDownloadOutline:
    "A cloud with a down arrow (ionicons style); downloads.",
  cloudUploadOutline: "A cloud with an up arrow (ionicons style); uploads.",

  // Feedback
  thumbUp: "A thumbs up; approval.",
  thumbDown: "A thumbs down; rejection.",
  handThumbUp: "A hand giving a thumbs up; endorsement.",

  // Domain-specific
  briefcase: "A briefcase; employment or professional info.",
  building: "A building; institutions or organizations.",
  certificate: "A certificate with a seal; certifications and submissions.",
  gavel: "A gavel; governance, groups, or decisions.",
  school: "A graduation cap; education.",
  robot: "A robot head; the chatbot.",
  code: "Angle brackets; code or technical configuration.",
  videoCamera: "A video camera; recordings or virtual meetings.",
  search: "A magnifying glass; search.",
} as const;

/** A valid icon name: a key of {@link PILLAR_ICON_DESCRIPTIONS}. */
export type PillarIconName = keyof typeof PILLAR_ICON_DESCRIPTIONS;

/** Every icon name, for pickers and validators. */
export const PILLAR_ICON_NAMES = Object.keys(
  PILLAR_ICON_DESCRIPTIONS,
) as PillarIconName[];

/**
 * Every icon name as a constant, so code refers to icons symbolically instead
 * of by loose string: `PILLAR_ICON.bookOutline` (the name) pairs with
 * `<PillarIcon.bookOutline />` (the component).
 */
export const PILLAR_ICON = Object.fromEntries(
  PILLAR_ICON_NAMES.map((iconName) => [iconName, iconName]),
) as { readonly [IconName in PillarIconName]: IconName };

export const isPillarIconName = (value: unknown): value is PillarIconName =>
  typeof value === "string" && value in PILLAR_ICON_DESCRIPTIONS;

/**
 * Narrows an icon name read from the database, where it is plain TEXT, to a
 * registered name; a stale or hand-edited value becomes `undefined` so callers
 * that look the name up in the registry never get a missing component.
 */
export const toPillarIconName = (
  value: string | null | undefined,
): PillarIconName | undefined => (isPillarIconName(value) ? value : undefined);
